import {
  Box,
  Button,
  Column,
  Columns,
  Dialog,
  Stack,
} from "@origin-digital/ods-core";
import React, { useEffect } from "react";
import { Contact, FormValues } from "../types";
import { Field, Form, Formik, FormikErrors, yupToFormErrors } from "formik";
import GeneralTextField from "./GeneralTextField";
import {
  electricianValidationSchema,
  plumberValidationSchema,
  supervisorValidationSchema,
} from "./Validations";
import { apiClient } from "src/api/apiService";
import { isEmptyString } from "../helper";
import { ChangeRequestFormValues } from "../ChangeRequest/formValue";

interface IAddContactProps {
  label: string;
  name:
    | "licensedElectrician"
    | "asp"
    | "siteSupervisor"
    | "gasLine.licensedPlumber"
    | "both.licensedPlumber"
    | "gasMeter.licensedPlumber";
  contactType: "ELECTRICIAN" | "SERVICE_PROVIDER" | "SUPERVISOR" | "PLUMBER";
  closeDialog: () => void;
  selectedContact?: Contact;
  fetchContacts: () => Promise<void>;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<ChangeRequestFormValues | FormValues>>;
  msaNumber?: string;
  state: string;
}

const initialContact: Contact = {
  email: "",
  first_name: "",
  id: "",
  last_name: "",
  license_number: "",
  phone_number: "",
};

const mapValidationSchemaByName: {
  [key: string]: any;
} = {
  licensedElectrician: electricianValidationSchema,
  asp: electricianValidationSchema,
  siteSupervisor: supervisorValidationSchema,
  "gasLine.licensedPlumber": plumberValidationSchema,
  "both.licensedPlumber": plumberValidationSchema,
  "gasMeter.licensedPlumber": plumberValidationSchema,
};

const AddContact: React.FC<IAddContactProps> = ({
  closeDialog,
  selectedContact,
  fetchContacts,
  setFieldValue,
  contactType,
  name,
  label,
  msaNumber,
  state,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [contact, setContact] = React.useState<Contact>(initialContact);
  const validationShema = mapValidationSchemaByName[name];
  useEffect(() => {
    if (selectedContact) setContact(selectedContact);
  }, []);

  const onAdd = async (values: Contact) => {
    setLoading(true);
    try {
      const {
        data: { id },
      } = await apiClient.post(`/contact/${msaNumber}`, {
        first_name: values.first_name,
        last_name: values.last_name,
        phone_number: values.phone_number,
        ...(isEmptyString(values.email) ? {} : { email: values.email }),
        ...(name.includes("licensedPlumber") ||
        name.includes("licensedElectrician")
          ? { license_number: values.license_number }
          : {}),
        //TODO: check what to do with this field
        service_provider: "",
        contact_type: contactType,
      });
      setLoading(false);
      closeDialog();
      await fetchContacts();
      setFieldValue(name, id);
    } catch (error) {
      console.log(`Add ${contactType} error: ${error}`);
      setLoading(false);
    }
  };

  const onEdit = async (values: Contact) => {
    setLoading(true);
    try {
      await apiClient.put(`/contact/${msaNumber}`, {
        id: values.id,
        first_name: values.first_name,
        last_name: values.last_name,
        phone_number: values.phone_number,
        ...(isEmptyString(values.email) ? {} : { email: values.email }),
        ...(name.includes("licensedPlumber") ||
        name.includes("licensedElectrician")
          ? { license_number: values.license_number }
          : {}),
        //TODO: check what to do with this field
        service_provider: "",
        contact_type: contactType,
      });
      setLoading(false);
      closeDialog();
      await fetchContacts();
    } catch (error) {
      console.log(`Edit ${contactType} error: ${error}`);
      setLoading(false);
    }
  };

  const onDelete = async () => {
    setLoading(true);
    try {
      await apiClient.put(`/contact/${selectedContact?.id}/deactivate`);
      setLoading(false);
      closeDialog();
      await fetchContacts();
      setFieldValue(name, "");
    } catch (error) {
      console.log(`Delete ${contactType} error: ${error}`);
      setLoading(false);
    }
  };

  return (
    <Dialog
      id="add-contract-dialog"
      open={true}
      title={selectedContact ? `Edit ${label}` : `Add new ${label}`}
      onClose={closeDialog}
    >
      <Formik<Contact>
        validationSchema={validationShema}
        validate={(values) => {
          let allErrors: any = {};
          try {
            validationShema.validateSync(values, {
              abortEarly: false,
              context: { state },
            });
          } catch (e) {
            allErrors = { ...yupToFormErrors(e) };
          }
          return allErrors;
        }}
        initialValues={selectedContact ? selectedContact : contact}
        onSubmit={(values) => {
          if (selectedContact) {
            onEdit(values);
          } else {
            onAdd(values);
          }
        }}
        validateOnChange={false}
        validateOnBlur={false}
      >
        <Form>
          <Stack space="small">
            {/* Box hack to fix issue with TextField label being cropped by Dialog title */}
            <Box />
            {/* End hack */}
            {/* Form fields */}
            <Stack space="xlarge">
              <Columns
                collapseBelow="sm"
                space={{
                  xs: "xlarge",
                  lg: "large",
                }}
              >
                <Column>
                  <Field
                    component={GeneralTextField}
                    name="first_name"
                    label="First name"
                  />
                </Column>
                <Column>
                  <Field
                    component={GeneralTextField}
                    name="last_name"
                    label="Last name"
                  />
                </Column>
              </Columns>
              {[
                "licensedElectrician",
                "gasLine.licensedPlumber",
                "both.licensedPlumber",
                "gasMeter.licensedPlumber",
              ].includes(name) && (
                <Field
                  component={GeneralTextField}
                  name="license_number"
                  label="License number"
                />
              )}
              <Field
                component={GeneralTextField}
                name="phone_number"
                label="Phone"
              />
              <Field component={GeneralTextField} name="email" label="Email" />
            </Stack>
            {/* End Form fields */}

            {/* Actions */}
            <Box
              position={{
                lg: "static",
                xs: "fixed",
              }}
              paddingTop={{
                lg: "medium",
                xs: "medium",
              }}
              paddingBottom={{
                lg: "none",
                xs: "medium",
              }}
              paddingX={{
                lg: "none",
                xs: "xlarge",
              }}
              backgroundColor="white"
              width="full"
              style={{
                left: "0",
                bottom: "0",
                zIndex: "1",
                //borderTop: "1px solid #E3E3E3",
              }}
              display={selectedContact ? "flex" : "block"}
              justifyContent="space-between"
            >
              {selectedContact && (
                <Button variant="text" color="primary" onClick={onDelete}>
                  Delete
                </Button>
              )}
              <Box
                display="flex"
                // flexWrap={{
                //   lg: "noWrap",
                //   xs: "wrap",
                // }}
                justifyContent="flex-end"
                alignItems="center"
              >
                <Box marginRight="large">
                  <Button variant="text" color="primary" onClick={closeDialog}>
                    Cancel
                  </Button>
                </Box>
                <Button type="submit" spinner={loading}>
                  Save person's data
                </Button>
              </Box>
            </Box>
            {/* End Actions */}
          </Stack>
        </Form>
      </Formik>
    </Dialog>
  );
};

export default AddContact;
